import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  Stack,
  Chip,
  FormControlLabel,
  Switch,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import { Calculate, Event, Timer } from '@mui/icons-material';
import { format, parseISO, addHours } from 'date-fns';
import toast from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import CaseAutocomplete from '../Common/CaseAutocomplete';
import { getAuthToken } from '../../lib/authToken';

interface CourtRule {
  id: string;
  name: string;
  description: string;
  mode: 'calendar' | 'business';
  defaultDays: number;
  direction: 'add' | 'subtract';
  extendIfWeekendHoliday: boolean;
}

interface CalculationResult {
  startDate: string;
  resultDate: string;
  resultDateISO: string;
  days: number;
  mode: string;
  direction: string;
  ruleId: string | null;
  ruleName: string | null;
  extended: boolean;
  steps: { step: string; detail: string }[];
  holidaysSkipped: { date: string; name: string }[];
}

interface SolPreset {
  id: string;
  name?: string;
  label?: string;
  description?: string;
  notes?: string;
  years?: number;
  months?: number;
  jurisdiction?: string;
  state?: string;
  claimType?: string;
}

interface SolResult {
  incidentDate: string;
  expirationDate: string;
  expirationDateISO?: string;
  presetId?: string;
  presetName?: string;
  years?: number;
  months?: number;
  tollingDays?: number;
  notes?: string;
}

interface DateCalculatorDialogProps {
  open: boolean;
  onClose: () => void;
  /** When set, SOL can be persisted to this case + calendar (Wave SL.3). */
  caseId?: string | null;
  onSolPersisted?: () => void;
  onCreateDeadline?: (prefill: {
    title: string;
    eventType: 'filing_deadline';
    startDate: string;
    endDate: string;
    allDay: boolean;
    notes: string;
  }) => void;
}

const DateCalculatorDialog: React.FC<DateCalculatorDialogProps> = ({
  open,
  onClose,
  caseId: lockedCaseId = null,
  onSolPersisted,
  onCreateDeadline,
}) => {
  const [tab, setTab] = useState(0);
  const [selectedCaseId, setSelectedCaseId] = useState<string>(lockedCaseId || '');
  const [persistLoading, setPersistLoading] = useState(false);
  const [createCalendarEvent, setCreateCalendarEvent] = useState(true);

  // Court rules state
  const [rules, setRules] = useState<CourtRule[]>([]);
  const [ruleId, setRuleId] = useState('frcp_6a_extend');
  const [startDate, setStartDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [days, setDays] = useState(14);
  const [direction, setDirection] = useState<'add' | 'subtract'>('add');
  const [mode, setMode] = useState<'calendar' | 'business'>('calendar');
  const [extend, setExtend] = useState(true);
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [title, setTitle] = useState('Filing deadline');

  // SOL state
  const [solPresets, setSolPresets] = useState<SolPreset[]>([]);
  const [solPresetsLoading, setSolPresetsLoading] = useState(false);
  const [incidentDate, setIncidentDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [presetId, setPresetId] = useState('');
  const [tollingDays, setTollingDays] = useState(0);
  const [customYears, setCustomYears] = useState<number | ''>('');
  const [customMonths, setCustomMonths] = useState<number | ''>('');
  const [solResult, setSolResult] = useState<SolResult | null>(null);
  const [solLoading, setSolLoading] = useState(false);
  const [solError, setSolError] = useState('');
  const [solTitle, setSolTitle] = useState('');

  useEffect(() => {
    if (!open) return;
    setTab(0);
    setResult(null);
    setSolResult(null);
    setError('');
    setSolError('');
    setSelectedCaseId(lockedCaseId || '');
    setCreateCalendarEvent(true);
  }, [open, lockedCaseId]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await fetch('/api/calendar/court-rules', {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        if (!res.ok) return;
        const json = await res.json();
        setRules(json.data?.rules || []);
      } catch {
        /* ignore */
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setSolPresetsLoading(true);
    (async () => {
      try {
        const res = await fetch('/api/calendar/sol-presets', {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        if (!res.ok) {
          throw new Error(await getApiErrorMessage(res, 'Unable to load SOL presets.'));
        }
        const json = await res.json();
        const presets: SolPreset[] = json.data?.presets || json.data || [];
        setSolPresets(Array.isArray(presets) ? presets : []);
        if (presets.length > 0 && !presetId) {
          setPresetId(presets[0].id);
        }
      } catch (err) {
        toast.error(toUserErrorMessage(err, 'Unable to load SOL presets.'));
      } finally {
        setSolPresetsLoading(false);
      }
    })();
  }, [open]);

  useEffect(() => {
    const rule = rules.find((r) => r.id === ruleId);
    if (!rule) return;
    setDays(rule.defaultDays);
    setDirection(rule.direction);
    setMode(rule.mode);
    setExtend(rule.extendIfWeekendHoliday);
    setResult(null);
  }, [ruleId, rules]);

  useEffect(() => {
    const preset = solPresets.find((p) => p.id === presetId);
    if (preset) {
      setSolTitle(`SOL — ${preset.label || preset.name || preset.id}`);
    }
  }, [presetId, solPresets]);

  const handleCalculate = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/calendar/calculate-deadline', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate,
          days,
          ruleId,
          direction,
          mode,
          extendIfWeekendHoliday: extend,
        }),
      });
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'Calculation failed'));
      }
      const json = await res.json();
      setResult(json.data);
    } catch (err) {
      const msg = toUserErrorMessage(err, 'Calculation failed');
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateSol = async () => {
    setSolError('');
    setSolLoading(true);
    try {
      const body: Record<string, unknown> = {
        incidentDate,
        tollingDays: tollingDays || 0,
      };
      if (presetId) body.presetId = presetId;
      if (customYears !== '') body.customYears = customYears;
      if (customMonths !== '') body.customMonths = customMonths;

      const res = await fetch('/api/calendar/calculate-sol', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'SOL calculation failed'));
      }
      const json = await res.json();
      setSolResult(json.data);
    } catch (err) {
      const msg = toUserErrorMessage(err, 'SOL calculation failed');
      setSolError(msg);
      toast.error(msg);
    } finally {
      setSolLoading(false);
    }
  };

  const handleCreateEvent = () => {
    if (!result || !onCreateDeadline) return;
    const deadlineStart = parseISO(result.resultDate);
    const startISO = format(deadlineStart, "yyyy-MM-dd'T'09:00");
    const endISO = format(addHours(deadlineStart, 1), "yyyy-MM-dd'T'HH:mm");
    const ruleLabel = result.ruleName || 'Court rule';
    onCreateDeadline({
      title: title || `Deadline — ${result.resultDate}`,
      eventType: 'filing_deadline',
      startDate: startISO,
      endDate: endISO,
      allDay: true,
      notes: [
        `Calculated via Date Calculator`,
        `Anchor: ${result.startDate}`,
        `Rule: ${ruleLabel}`,
        `Mode: ${result.mode} / ${result.direction} ${result.days} day(s)`,
        result.extended ? 'Extended to next business day (weekend/holiday)' : null,
        ...result.steps.map((s) => s.detail),
      ].filter(Boolean).join('\n'),
    });
    onClose();
  };

  const handleCreateSolReminder = () => {
    if (!solResult || !onCreateDeadline) return;
    const expDate = solResult.expirationDateISO || solResult.expirationDate;
    const deadlineStart = parseISO(expDate.slice(0, 10));
    const startISO = format(deadlineStart, "yyyy-MM-dd'T'09:00");
    const endISO = format(addHours(deadlineStart, 1), "yyyy-MM-dd'T'HH:mm");
    const matched = solPresets.find((p) => p.id === presetId);
    const presetLabel =
      solResult.presetName ||
      matched?.label ||
      matched?.name ||
      'Statute of limitations';
    onCreateDeadline({
      title: solTitle || `SOL — ${presetLabel}`,
      eventType: 'filing_deadline',
      startDate: startISO,
      endDate: endISO,
      allDay: true,
      notes: [
        'Calculated via Statute of Limitations tab',
        `Incident date: ${solResult.incidentDate}`,
        `Preset: ${presetLabel}`,
        solResult.tollingDays ? `Tolling days: ${solResult.tollingDays}` : null,
        solResult.notes || null,
        'Illustrative only — not legal advice',
      ].filter(Boolean).join('\n'),
    });
    onClose();
  };

  const handlePersistSol = async () => {
    if (!solResult || !selectedCaseId) {
      toast.error('Select a case to save the SOL deadline.');
      return;
    }
    setPersistLoading(true);
    try {
      const body: Record<string, unknown> = {
        caseId: selectedCaseId,
        incidentDate,
        createCalendarEvent,
        tollingDays: tollingDays || 0,
      };
      if (presetId) body.presetId = presetId;
      if (customYears !== '') body.customYears = customYears;
      if (customMonths !== '') body.customMonths = customMonths;

      const res = await fetch('/api/calendar/persist-sol', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error(await getApiErrorMessage(res, 'Failed to save SOL to case'));
      }
      toast.success(
        createCalendarEvent
          ? 'SOL saved to case and calendar deadline created'
          : 'SOL saved to case'
      );
      onSolPersisted?.();
      onClose();
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Failed to save SOL to case'));
    } finally {
      setPersistLoading(false);
    }
  };

  const selectedPreset = solPresets.find((p) => p.id === presetId);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Calculate color="primary" />
        Date Calculator
      </DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Court rules" />
        <Tab label="Statute of limitations" icon={<Timer fontSize="small" />} iconPosition="start" />
      </Tabs>
      <DialogContent dividers>
        {tab === 0 && (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}

            <FormControl fullWidth>
              <InputLabel>Court rule</InputLabel>
              <Select
                label="Court rule"
                value={ruleId}
                onChange={(e) => setRuleId(e.target.value)}
              >
                {rules.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {rules.find((r) => r.id === ruleId)?.description && (
              <Typography variant="body2" color="text.secondary">
                {rules.find((r) => r.id === ruleId)?.description}
              </Typography>
            )}

            <TextField
              label="Anchor date (service / trigger / hearing)"
              type="date"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Days"
                type="number"
                fullWidth
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value, 10) || 0)}
                inputProps={{ min: 0 }}
              />
              <FormControl fullWidth>
                <InputLabel>Direction</InputLabel>
                <Select
                  label="Direction"
                  value={direction}
                  onChange={(e) => setDirection(e.target.value as 'add' | 'subtract')}
                >
                  <MenuItem value="add">Add (forward)</MenuItem>
                  <MenuItem value="subtract">Subtract (backward)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Count</InputLabel>
                <Select
                  label="Count"
                  value={mode}
                  onChange={(e) => setMode(e.target.value as 'calendar' | 'business')}
                >
                  <MenuItem value="calendar">Calendar days</MenuItem>
                  <MenuItem value="business">Business days</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <FormControlLabel
              control={<Switch checked={extend} onChange={(e) => setExtend(e.target.checked)} />}
              label="Extend if deadline falls on weekend / federal holiday (FRCP 6(a))"
            />

            <Button variant="contained" onClick={handleCalculate} disabled={loading}>
              {loading ? 'Calculating…' : 'Calculate deadline'}
            </Button>

            {result && (
              <>
                <Divider />
                <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                  <Typography variant="overline" color="text.secondary">
                    Result
                  </Typography>
                  <Typography variant="h4" sx={{ fontFamily: 'monospace', mb: 1 }}>
                    {format(parseISO(result.resultDate), 'EEEE, MMM d, yyyy')}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    <Chip size="small" label={`${result.days} ${result.mode} day(s)`} />
                    <Chip size="small" label={result.direction} />
                    {result.extended && <Chip size="small" color="warning" label="Extended" />}
                    {result.ruleName && <Chip size="small" color="primary" label={result.ruleName} />}
                  </Stack>
                  {result.steps.map((s, i) => (
                    <Typography key={i} variant="caption" display="block" color="text.secondary">
                      {s.detail}
                    </Typography>
                  ))}
                  {result.holidaysSkipped.length > 0 && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Holidays in range:{' '}
                      {result.holidaysSkipped.map((h) => `${h.name} (${h.date})`).join(', ')}
                    </Typography>
                  )}
                </Box>

                {onCreateDeadline && (
                  <TextField
                    label="Deadline event title"
                    fullWidth
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                )}
              </>
            )}
          </Stack>
        )}

        {tab === 1 && (
          <Stack spacing={2}>
            {solError && <Alert severity="error">{solError}</Alert>}

            <Alert severity="warning" variant="outlined">
              Illustrative only — not legal advice. Verify SOL periods with current statutes and
              counsel before relying on calculated dates.
            </Alert>

            {lockedCaseId ? (
              <Alert severity="info" variant="outlined">
                Saving will write the SOL to this matter and optionally create a calendar deadline.
              </Alert>
            ) : (
              <CaseAutocomplete
                label="Case (required to save SOL)"
                placeholder="Search cases…"
                value={selectedCaseId || null}
                onChange={(c) => setSelectedCaseId(c?.id || '')}
              />
            )}

            <TextField
              label="Incident / accrual date"
              type="date"
              fullWidth
              value={incidentDate}
              onChange={(e) => setIncidentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <FormControl fullWidth disabled={solPresetsLoading}>
              <InputLabel>SOL preset</InputLabel>
              <Select
                label="SOL preset"
                value={presetId}
                onChange={(e) => setPresetId(e.target.value)}
              >
                {solPresets.length === 0 ? (
                  <MenuItem value="" disabled>
                    {solPresetsLoading ? 'Loading…' : 'No presets available'}
                  </MenuItem>
                ) : (
                  solPresets.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.label || p.name || p.id}
                      {(p.state || p.jurisdiction) ? ` (${p.state || p.jurisdiction})` : ''}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {(selectedPreset?.notes || selectedPreset?.description) && (
              <Typography variant="body2" color="text.secondary">
                {selectedPreset.notes || selectedPreset.description}
                {(selectedPreset.years != null || selectedPreset.months != null) && (
                  <>
                    {' '}
                    — Default:{' '}
                    {[selectedPreset.years ? `${selectedPreset.years} yr` : null, selectedPreset.months ? `${selectedPreset.months} mo` : null]
                      .filter(Boolean)
                      .join(', ')}
                  </>
                )}
              </Typography>
            )}

            <TextField
              label="Tolling days (optional)"
              type="number"
              fullWidth
              value={tollingDays}
              onChange={(e) => setTollingDays(parseInt(e.target.value, 10) || 0)}
              inputProps={{ min: 0 }}
              helperText="Additional days added to the limitation period"
            />

            <Typography variant="subtitle2" color="text.secondary">
              Custom override (optional)
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Years"
                type="number"
                fullWidth
                value={customYears}
                onChange={(e) =>
                  setCustomYears(e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0)
                }
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Months"
                type="number"
                fullWidth
                value={customMonths}
                onChange={(e) =>
                  setCustomMonths(e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0)
                }
                inputProps={{ min: 0 }}
              />
            </Stack>

            <Button
              variant="contained"
              onClick={handleCalculateSol}
              disabled={solLoading || !incidentDate}
            >
              {solLoading ? 'Calculating…' : 'Calculate expiration'}
            </Button>

            {solResult && (
              <>
                <Divider />
                <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                  <Typography variant="overline" color="text.secondary">
                    Expiration date
                  </Typography>
                  <Typography variant="h4" sx={{ fontFamily: 'monospace', mb: 1 }}>
                    {format(
                      parseISO((solResult.expirationDateISO || solResult.expirationDate).slice(0, 10)),
                      'EEEE, MMM d, yyyy'
                    )}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    {solResult.presetName && (
                      <Chip size="small" color="primary" label={solResult.presetName} />
                    )}
                    {solResult.tollingDays ? (
                      <Chip size="small" label={`+${solResult.tollingDays} tolling days`} />
                    ) : null}
                  </Stack>
                  {solResult.notes && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {solResult.notes}
                    </Typography>
                  )}
                  <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Illustrative only — not legal advice
                  </Typography>
                </Box>

                {onCreateDeadline && (
                  <TextField
                    label="Calendar reminder title (optional local event)"
                    fullWidth
                    value={solTitle}
                    onChange={(e) => setSolTitle(e.target.value)}
                  />
                )}

                <FormControlLabel
                  control={
                    <Switch
                      checked={createCalendarEvent}
                      onChange={(e) => setCreateCalendarEvent(e.target.checked)}
                    />
                  }
                  label="Also create calendar filing deadline when saving to case"
                />
              </>
            )}

            {!solPresetsLoading && solPresets.length === 0 && (
              <Alert severity="info">
                No SOL presets configured. Use custom years/months or contact your administrator.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose}>Close</Button>
        {tab === 0 && result && onCreateDeadline && (
          <Button variant="contained" startIcon={<Event />} onClick={handleCreateEvent}>
            Create filing deadline
          </Button>
        )}
        {tab === 1 && solResult && onCreateDeadline && (
          <Button variant="outlined" startIcon={<Event />} onClick={handleCreateSolReminder}>
            Create local calendar reminder
          </Button>
        )}
        {tab === 1 && solResult && (
          <Button
            variant="contained"
            startIcon={<Timer />}
            onClick={handlePersistSol}
            disabled={persistLoading || !selectedCaseId}
          >
            {persistLoading ? 'Saving…' : 'Save SOL to case + calendar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DateCalculatorDialog;
